<script lang="ts" setup>
/*=============================================================================*/
/*                               Imports                                       */
/*=============================================================================*/
import { useIrveStore } from "@modules/irve/stores/irve.store"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "primevue/usetoast"
import { formatDate } from "@core/helpers"

const irveStore = useIrveStore()

// instancie le gestionnaire de l'appel API pour modifier le nom de l'analyse
const editAnalyseName = irveStore.getEditAnalysisNameFetch()

const modelAnalysis = ref<string | null>(null)

/*=============================================================================*/
/*                                  Lang                                       */
/*=============================================================================*/
const { t } = useI18n({
    messages: {
        fr: {
            default_name: "Analyse sans nom",
            title: "Dernière analyse effectuée",
            button: "Télécharger le rapport",
            select: "Sélectionner une analyse",
            analysis: {
                period: "Période de l'analyse : {start} - {end}",
                created_at: "Réalisée le : {created}",
            },
            failure: "Analyse échouée",
        },
    },
})

/*=============================================================================*/
/*                                  Tools                                      */
/*=============================================================================*/

const modelName = ref(irveStore.getCurrentAnalysis?.name || "")
const toast = useToast()

/**
 * Quand l'utilisateur valide le nouveau nom de l'analyse
 */
const onValidNewName = async (closeCallback: () => void) => {
    closeCallback()

    // compare le nom actuel de l'analyse avec le nouveau nom
    if (modelName.value === irveStore.getCurrentAnalysis?.name) {
        return
    }

    // appel de l'API pour modifier le nom de l'analyse
    await editAnalyseName.fetch({
        _id_analysis: irveStore.getCurrentAnalysis?._id_analysis,
        name: modelName.value,
    })
    if (editAnalyseName.hasError.value) {
        toast.add({
            severity: "error",
            summary: "Erreur",
            detail: "Une erreur est survenue lors de la modification du nom",
            life: 3000,
        })
    }
}

/**
 * Récupère le nom de l'analyse
 */
const getName = computed(() => {
    return irveStore.getCurrentAnalysis?.name
        ? irveStore.getCurrentAnalysis.name
        : t("default_name")
})

/**
 * Récupère la période de l'analyse
 */
const getPeriod = computed(() => {
    if (!irveStore.getCurrentAnalysis) return ""

    return t("analysis.period", {
        start: formatDate(irveStore.getCurrentAnalysis.inputs.date_from),
        end: formatDate(irveStore.getCurrentAnalysis.inputs.date_to),
    })
})

/**
 * Récupère la date de création de l'analyse
 */
const getCreatedAt = computed(() => {
    if (!irveStore.getCurrentAnalysis) return ""

    return t("analysis.created_at", {
        created: formatDate(irveStore.getCurrentAnalysis.created_at),
    })
})

const getAnalysesOptionsList = computed(() => {
    return irveStore.analyses?.map((analysis) => ({
        label: `${analysis.name} - ${formatDate(analysis.created_at)}`,
        value: analysis._id_analysis,
    }))
})

watch(
    () => irveStore.currentAnalysisId,
    (value) => {
        if (value) {
            modelAnalysis.value = value
        }
    },
    {
        immediate: true,
        deep: true,
    }
)

function onUpdateModelAnalysis(value: string) {
    modelAnalysis.value = value
    irveStore.viewAnalysis(value)
}
</script>

<template>
    <div class="irve-view-header flex flex-col gap-1.5">
        <div class="flex flex-row items-center justify-between">
            <div class="flex flex-row items-center gap-2">
                <h1 class="font-bold text-primary-700 text-lg">
                    {{ t("title") }}
                </h1>
                <BaseTag
                    v-if="irveStore.getCurrentAnalysis?.status === 'FAILURE'"
                    severity="warn"
                    >{{ t("failure") }}</BaseTag
                >
            </div>
            <BaseSelect
                class="ml-auto"
                :options="getAnalysesOptionsList"
                option-label="label"
                option-value="value"
                :loading="irveStore.fetchAnalyses.isFetching"
                :disabled="irveStore.fetchAnalyses.isFetching"
                :model-value="modelAnalysis"
                :placeholder="t('select')"
                @update:model-value="onUpdateModelAnalysis"
            />
        </div>
        <div
            class="flex flex-row items-center gap-2 overflow-x-auto"
            v-if="!irveStore.isFetchingAnalysis"
        >
            <BaseInplace>
                <template #display>
                    <p>
                        {{ getName }}
                        <i class="pi pi-pencil ml-2 text-cyan-400"></i>
                    </p>
                </template>
                <template #content="{ closeCallback }">
                    <span class="inline-flex items-center gap-2">
                        <BaseInputText v-model="modelName" autofocus />
                        <BaseButton
                            icon="pi pi-check"
                            text
                            severity="success"
                            @click="onValidNewName(closeCallback)"
                        />
                        <BaseButton
                            icon="pi pi-times"
                            text
                            severity="danger"
                            @click="closeCallback"
                        />
                    </span>
                </template>
            </BaseInplace>
            <span class="text-cyan-400">&#9679;</span>
            <p class="text-primary-700">{{ getPeriod }}</p>
            <span class="text-cyan-400">&#9679;</span>
            <p class="text-primary-700">
                {{ getCreatedAt }}
            </p>
        </div>
        <div v-else class="flex flex-row items-center gap-2 overflow-x-auto">
            <BaseSkeleton
                v-for="(skeleton, index) in [
                    ['8rem', '1.5rem'],
                    ['1rem', '1.5rem'],
                    ['12rem', '1.5rem'],
                    ['1rem', '1.5rem'],
                    ['12rem', '1.5rem'],
                ]"
                :key="index"
                :width="skeleton[0]"
                :height="skeleton[1]"
                class="mt-2"
            ></BaseSkeleton>
        </div>
        <BaseToast />
    </div>
</template>
